import React, { useCallback, useEffect, useState } from 'react';

import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { type BridgeRelayDecision } from '@atlaskit/rovo-triggers/extension-context-bridge/analytics';
import { useSubscribeAll } from '@atlaskit/rovo-triggers/main';
import { getRovoParams } from '@atlaskit/rovo-triggers/params';
import { type RovoChatOpenMode } from '@atlaskit/rovo-triggers/params-types';
import { RovoPostMessagePubsubListener } from '@atlaskit/rovo-triggers/post-message-to-pubsub';
import { type Payload } from '@atlaskit/rovo-triggers/types';
import { useRovoEntitlement } from '@atlassian/conversation-assistant-entitlement/controllers/rovo-entitlement';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useChatContextStoreActions } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import { useUIStoreActions } from '@atlassian/conversation-assistant-store/controllers/ui-store';

import { isJiraDeferredSeededChatOpenEnabled } from '../../common/utils/isJiraDeferredSeededChatOpenEnabled';

type Props = {
	onTriggerOpeningChat: (
		conversationId?: string,
		chatOpenMode?: RovoChatOpenMode,
		source?: string,
		autoFocusInput?: boolean,
	) => void;
	/**
	 * Opt in to mirroring this product's context to the Rovo browser-extension chat.
	 *
	 * @default false
	 */
	extensionContextBridgeEnabled?: boolean;
};

// TODO: Deprecated; teams integrating this event should opt-out of chat open using `openChat` on the core event payload
// Once all teams have opted out the ignoredEvents logic can be removed
const ignoredEvents = new Set<Payload['type']>([
	'editor-context-payload',
	'browser-context-payload',
	'agent-changed',
]);

/**
 * Lazy-loaded so products that don't opt in never fetch this chunk — `ChatOpenerSubscriber` mounts
 * at the root of every product that embeds Rovo chat, so a static import would add it to every
 * page-load bundle regardless.
 */
const LazyExtensionContextBridgeHost = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_rovo-triggers_extension-context-bridge-host" */ '@atlaskit/rovo-triggers/extension-context-bridge/host'
	).then(({ ExtensionContextBridgeHost }) => ({ default: ExtensionContextBridgeHost })),
);

const eventsToSkipAnalytics = new Set<Payload['type']>([
	// This event is fired way too often https://atlassian.slack.com/archives/C06PKQPN8BF/p1729569226011399
	'editor-context-payload',
	'agent-changed',
]);

/**
 * This component is meant to be used at root level of the app
 * Outside chat sidebar, but under the assumption that the chat sidebar exists in that app
 *
 * 1. This component listens to all pubsub events emitted
 * for specific events, it will call `onEventTriggersChatOpen`
 *
 * 2. It's also used to subscribe to `rovoChatTriggerOpen` params!
 *
 * 3. Then it also handles `RovoPostMessagePubsubListener` to listen to postMessage events targeted for chat sidebar
 *
 * Example use case: if Rovo chat is closed in Confluence sidebar
 * and there is a reading-aids follow up triggering 'chat-new' pubsub event
 * We want to open the chat, and create a new conversation following that
 *
 * This component will be responsible to handle opening the chat, by calling `onEventTriggersChatOpen`
 * Each product will pass their own callback on how to open chat
 */
export const ChatOpenerSubscriber = ({
	onTriggerOpeningChat,
	extensionContextBridgeEnabled = false,
}: Props): React.JSX.Element => {
	const { sendAnalyticsEvent } = useAnalytics();
	const { setDeferredSeededConversationPending } = useUIStoreActions();

	// Matches EXT_CONTEXT_BRIDGE_EXPERIMENT in
	// `@atlaskit/rovo-triggers/extension-context-bridge/constants`.
	//
	// The consumer opt-in stays the outer condition, so a product that has not opted in never reads
	// the experiment at all. The read itself is no-exposure because this component mounts at the root
	// of every page of an opted-in product: exposure here would count product page loads rather than
	// extension users. The extension logs the one exposure.
	const isExtensionContextBridgeEnabled =
		extensionContextBridgeEnabled &&
		UNSAFE_expValNoExposure('rovo-ext_context_bridge_exp', 'isEnabled', false) === true;

	// Mounted after hydration: a lazy Host rendered during hydration leaves a dehydrated Suspense
	// boundary that the next parent re-render breaks (React #421).
	const [isBridgeHostMountable, setIsBridgeHostMountable] = useState(false);
	useEffect(() => {
		if (isExtensionContextBridgeEnabled) {
			setIsBridgeHostMountable(true);
		}
	}, [isExtensionContextBridgeEnabled]);

	const { getTarget } = useChatContextStoreActions();
	/**
	 * Resolves the content id the bridge Host stamps onto outbound `editor-context-payload`. Resolved
	 * here rather than in the Host to avoid a `conversation-assistant-store` dependency cycle in
	 * `@atlaskit/rovo-triggers`.
	 */
	const getBridgeContentId = useCallback((): string | undefined => {
		const target = getTarget('global');
		return typeof target?.id === 'string' ? target.id : target?.id?.toString();
	}, [getTarget]);

	/**
	 * Read here for the same reason as `getBridgeContentId`, and passed to the Host so a tenant with
	 * AI turned off relays nothing (SECASR-8597). Products set this via `RovoEntitlementSetter`
	 * alongside their nav button, so it is populated on every page the Host mounts on.
	 */
	const { isRovoEnabled } = useRovoEntitlement();

	/**
	 * Reports what the bridge's relay policy decided for each payload this product published.
	 *
	 * Fired here rather than in `@atlaskit/rovo-triggers`, which has no analytics dependency, for the
	 * same reason as `getBridgeContentId`. Lands in the host product's own stream, which is right:
	 * these are the product's publishers. The bridge deduplicates to one call per distinct outcome
	 * per page.
	 *
	 * Data Portal registry: https://data-portal.internal.atlassian.com/analytics/registry/109870
	 */
	const handleBridgeRelayDecision = useCallback(
		(decision: BridgeRelayDecision): void => {
			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'evaluated',
				actionSubject: 'rovoExtensionBridgePayload',
				source: decision.publisherSource,
				attributes: {
					payloadType: decision.payloadType,
					contextKey: decision.contextKey,
					relayed: decision.relayed,
					relayDecision: decision.decision,
					publisherSource: decision.publisherSource,
					payloadProduct: decision.payloadProduct,
				},
			});
		},
		[sendAnalyticsEvent],
	);

	const [triggerOpen, setTriggerOpen] = useState<boolean>(!!getRovoParams()?.triggerOpen);

	// Trigger open rovo sidebar if not already open via URL param
	useEffect(() => {
		try {
			if (!triggerOpen) {
				return;
			}
			onTriggerOpeningChat();
			setTriggerOpen(false);
		} catch (error) {
			// Fail silently
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [triggerOpen]);

	useSubscribeAll((payload) => {
		const { type, source, openChat = true } = payload;
		const deferOpenUntilSeeded =
			type === 'chat-new' &&
			payload.data.deferOpenUntilSeeded === true &&
			!payload.data.skipCreatingSeededConversation &&
			isJiraDeferredSeededChatOpenEnabled();

		if (deferOpenUntilSeeded && openChat) {
			setDeferredSeededConversationPending(source);
		}

		const preventOpen = !openChat || ignoredEvents.has(type);

		if (!eventsToSkipAnalytics.has(type)) {
			sendAnalyticsEvent({
				action: 'eventReceived',
				actionSubject: 'aiMatePubSubEvent',
				source,
				attributes: {
					publisherSource: source,
					payloadType: type,
					eventTriggersChatOpen: !preventOpen,
				},
			});
		}

		if (preventOpen) {
			return;
		}

		// Extract conversation ID from chat-open events
		const conversationId =
			type === 'chat-open' && 'data' in payload ? payload.data.channelId : undefined;

		const openChatMode = 'openChatMode' in payload ? payload.openChatMode : undefined;
		const autoFocusInput = 'autoFocusInput' in payload ? payload.autoFocusInput : undefined;

		if (fg('fix-jira-ai-plugin-dropdown-overlap')) {
			onTriggerOpeningChat(conversationId, openChatMode, source, autoFocusInput);
		} else {
			onTriggerOpeningChat(conversationId, openChatMode, undefined, autoFocusInput);
		}
	});

	return (
		<>
			<RovoPostMessagePubsubListener />
			{/* The Host self-gates too; checking here avoids fetching the chunk unnecessarily. */}
			{isExtensionContextBridgeEnabled && isBridgeHostMountable && (
				<React.Suspense fallback={null}>
					<LazyExtensionContextBridgeHost
						getContentId={getBridgeContentId}
						isAiEnabled={isRovoEnabled}
						onRelayDecision={handleBridgeRelayDecision}
					/>
				</React.Suspense>
			)}
		</>
	);
};
